import { useEffect } from "react";
import { Link } from "react-router-dom";
import LicensingClause from "../components/licensing/LicensingClause";
import PageMeta from "../components/seo/PageMeta";
import {
  LICENSING_LAST_UPDATED,
  LICENSING_REQUEST_HREF,
  LICENSING_SUMMARY,
} from "../data/licensing";
import { buildWebPageJsonLd } from "../lib/seo";
import "../styles/licensing.css";

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2 4 5v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V5z" />
    </svg>
  );
}

function EnvelopeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m3 6 9 7 9-7" />
    </svg>
  );
}

const SUMMARY_ICONS = {
  check: CheckIcon,
  alert: AlertIcon,
  shield: ShieldIcon,
};

export default function LicensingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <article className="licensing-page" aria-label="Licensing">
      <PageMeta
        title="Licensing"
        description="How you can use CJ Jayamaha's photography — personal use, commercial licensing, attribution, and copyright terms in plain English."
        path="/licensing"
        keywords={["licensing", "copyright", "commercial use", "photography licence", "CJ Jayamaha"]}
        jsonLd={buildWebPageJsonLd({
          title: "Photography Licensing",
          description:
            "Personal and commercial licensing terms for photography by CJ Jayamaha.",
          path: "/licensing",
        })}
      />
      <header className="licensing-page__header">
        <div className="licensing-page__eyebrow">LICENSING</div>
        <h1 className="licensing-page__title">
          How you can
          <br />
          use my work.
        </h1>
        <p className="licensing-page__intro">
          The short version: every photograph on this site is for your{" "}
          <strong>personal enjoyment</strong>. You&rsquo;re welcome to print it,
          frame it, and live with it. What you can&rsquo;t do is use it to make
          money or promote a business without asking me first. The longer version
          — the actual agreement — is below.
        </p>
        <div className="licensing-page__updated">{LICENSING_LAST_UPDATED}</div>
      </header>

      <section className="licensing-summary" aria-label="Summary">
        <div className="licensing-summary__grid">
          {LICENSING_SUMMARY.map((card) => {
            const Icon = SUMMARY_ICONS[card.icon];
            return (
              <div key={card.key} className="licensing-summary__card">
                <span className="licensing-summary__icon">
                  <Icon />
                </span>
                <div className="licensing-summary__title">{card.title}</div>
                <p className="licensing-summary__blurb">{card.blurb}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="licensing-agreement" aria-labelledby="licensing-agreement-heading">
        <div id="licensing-agreement-heading" className="licensing-agreement__label">
          THE AGREEMENT
        </div>

        <LicensingClause number="01" title="Ownership & copyright">
          <p className="licensing-clause__text">
            All photographs, images, and related materials on this site
            (&ldquo;the Work&rdquo;) are created by and remain the exclusive
            intellectual property of CJ Jayamaha. I retain full copyright and all
            moral and reproduction rights at all times, in line with the
            Copyright, Designs and Patents Act 1988 and applicable international
            law. Purchasing a digital file, print, or framed piece transfers
            ownership of <em>that copy</em> only — it does not transfer copyright,
            reproduction rights, or any ownership of the underlying image.
          </p>
        </LicensingClause>

        <LicensingClause number="02" title="Your personal-use licence">
          <p className="licensing-clause__text">
            When you buy a photograph — digital or physical — you receive a
            non-exclusive, non-transferable licence to use it for personal,
            non-commercial purposes. This licence lets you:
          </p>
          <ul className="licensing-clause__list">
            <li>Display the Work in your home, office, or personal living space.</li>
            <li>
              Print a digital file for your own personal use (within the resolution
              you purchased).
            </li>
            <li>Use a digital file as personal wallpaper on your own devices.</li>
            <li>Give a print or framed piece as a personal gift.</li>
          </ul>
        </LicensingClause>

        <LicensingClause number="03" title="What's not permitted">
          <p className="licensing-clause__text">
            Without my prior written permission, you may <strong>not</strong>:
          </p>
          <ul className="licensing-clause__list">
            <li>
              Use the Work for any commercial, promotional, or revenue-generating
              purpose.
            </li>
            <li>
              Reproduce, copy, resell, redistribute, or sub-license the Work or
              any file.
            </li>
            <li>
              Edit, crop, recolour, apply filters, or create derivative works from
              the Work.
            </li>
            <li>
              Remove, obscure, or alter any signature, watermark, or copyright
              notice.
            </li>
            <li>
              Upload the Work to stock libraries, NFT platforms, print-on-demand
              services, or AI training datasets.
            </li>
            <li>Claim authorship of, or imply you created, the Work.</li>
          </ul>
        </LicensingClause>

        <LicensingClause number="04" title="Commercial use">
          <p className="licensing-clause__text">
            Any use connected to business, profit, or promotion is considered
            commercial — including advertising, marketing, packaging, editorial,
            websites, social media for a brand, merchandise, books, or anything
            offered for sale. Commercial use is available, but only under a
            separate written commercial licence agreed in advance. Terms, scope,
            duration, and fees are negotiated per project. If you&rsquo;d like to
            use an image commercially,{" "}
            <Link to="/contact" className="licensing-clause__link">
              get in touch
            </Link>{" "}
            and we&rsquo;ll work it out properly.
          </p>
        </LicensingClause>

        <LicensingClause number="05" title="Attribution">
          <p className="licensing-clause__text">
            Attribution isn&rsquo;t required for private personal use, but
            it&rsquo;s always appreciated. If you share an image in any public
            personal context — a personal blog or social post — a simple credit
            to &ldquo;CJ Jayamaha / cj.photos&rdquo; is the kind thing to do. For
            any permitted commercial use, attribution terms will be set out in
            the relevant licence.
          </p>
        </LicensingClause>

        <LicensingClause number="06" title="Breach & termination">
          <p className="licensing-clause__text">
            This licence is granted on the condition that its terms are respected.
            Any unauthorised or commercial use without permission automatically
            terminates your licence and constitutes copyright infringement, which
            may be pursued under applicable law — including claims for damages
            and licensing fees backdated to the start of the infringing use. On
            termination you must stop using and delete any digital copies of the
            Work.
          </p>
        </LicensingClause>

        <LicensingClause number="07" title="Governing terms">
          <p className="licensing-clause__text">
            This agreement is governed by the laws of England &amp; Wales. These
            terms may be updated from time to time; the version in force is the
            one published here at the date of your purchase. If any provision is
            found unenforceable, the remaining provisions stay in full effect.
            Nothing here limits your statutory consumer rights.
          </p>
        </LicensingClause>
      </section>

      <section className="licensing-cta" aria-label="Commercial licensing">
        <div className="licensing-cta__panel">
          <div className="licensing-cta__copy">
            <div className="licensing-cta__title">
              Want to use an image commercially?
            </div>
            <p className="licensing-cta__blurb">
              Tell me which photo and how you&rsquo;d like to use it, and
              I&rsquo;ll put together a licence that works for both of us.
            </p>
          </div>
          <a href={LICENSING_REQUEST_HREF} className="licensing-cta__button">
            <EnvelopeIcon />
            REQUEST A LICENCE
          </a>
        </div>
      </section>
    </article>
  );
}
